import React, { useEffect, useMemo, useState } from 'react';

import useProcedureStore from '../../stores/procedureStore';
import useLabOrderStore from '../../stores/labOrderStore';
import LabReportActions from '../lab/LabReportActions';
import ValidityControl, { plusDaysISO } from '../common/ValidityControl';
import { labStatusLabel, isStandingLabOrder } from '../../constants/lab';
import { lastDoneByProcedure, lastDoneLabel } from './labSummaryProjection';
import { performLabOrder, updateLabReviewDate, cancelLabOrder } from '../../services/api/labApi';
import { procedurePickerDeptAnchor } from './departmentResolution';

/**
 * LabAdvisePanel — the doctor's "advise a lab test" surface, shown as the Lab
 * tab on the visit. The doctor only picks the test from the catalog and
 * advises it; the order is created UNASSIGNED (`advised`). A receptionist
 * assigns it to a technician later — the doctor never picks the technician.
 */
export default function LabAdvisePanel({ visit, includeAdvised = false, includeCompleted = false, showList = true, readOnly = false }) {
  // A5 — the "＋ Add lab test…" picker is dept-SCOPED to the visit's department
  // (server-scoped, dept-tagged ∪ org-wide). Falsy/EMERGENCY anchor → null →
  // full catalog. Never reads the unscoped admin `procedures` slice.
  const pickerProcedures = useProcedureStore((s) => s.pickerProcedures);
  const fetchProceduresForDept = useProcedureStore((s) => s.fetchProceduresForDept);
  const anchor = procedurePickerDeptAnchor(visit);
  const { byVisit, byPatient, advise, fetchForVisit } = useLabOrderStore();
  const lastDone = useMemo(
    () => lastDoneByProcedure(byPatient[visit?.patient_id]?.orders || []),
    [byPatient, visit?.patient_id]
  );

  const [testId, setTestId] = useState('');
  const [expiresAt, setExpiresAt] = useState(plusDaysISO(30));
  const [isStanding, setIsStanding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const visitId = visit?.visit_id;
  const labTests = useMemo(
    () => pickerProcedures.filter((p) => p.category === 'lab' && p.active !== false),
    [pickerProcedures]
  );
  const pickedTest = useMemo(
    () => labTests.find((t) => t.procedure_id === testId) || null,
    [labTests, testId]
  );
  const advised = byVisit[visitId] || [];

  useEffect(() => {
    fetchProceduresForDept(anchor);
    if (visitId) fetchForVisit(visitId, { includeAdvised, includeCompleted });
  }, [fetchProceduresForDept, anchor, fetchForVisit, visitId, includeAdvised, includeCompleted]);

  const submit = async () => {
    if (!testId) {
      setMsg({ k: 'err', t: 'Pick a lab test first.' });
      return;
    }
    setBusy(true);
    setMsg(null);
    const r = await advise({
      visit_id: visitId,
      patient_id: visit?.patient_id,
      catalog_procedure_id: testId,
      patient_name: visit?.patient_name || visit?.name || undefined,
      queue_number: visit?.queue_number,
      // Expiry window — "Valid until" (or review date when standing).
      expires_at: expiresAt || null,
      standing: isStanding,
    });
    setBusy(false);
    if (r.success) {
      setTestId('');
      setExpiresAt(plusDaysISO(30));
      setIsStanding(false);
      setMsg({ k: 'ok', t: 'Lab test advised — reception will assign a technician.' });
    } else {
      setMsg({ k: 'err', t: r.error || 'Failed to advise lab test.' });
    }
  };

  // ── standing-order management (perform again / renew / discontinue) ──
  const [reviewEdit, setReviewEdit] = useState(null); // { id, date }
  const refresh = () => (visitId ? fetchForVisit(visitId, { includeAdvised, includeCompleted }) : null);

  const onPerform = async (o) => {
    setMsg(null);
    const r = await performLabOrder(o.lab_order_id, { visit_id: visitId });
    if (r.success) { await refresh(); setMsg({ k: 'ok', t: 'Performed — a fresh test was created for the technician.' }); }
    else setMsg({ k: 'err', t: r.error || 'Could not perform the test.' });
  };
  const onSaveReview = async (o) => {
    const date = reviewEdit?.date;
    if (!date) return;
    const r = await updateLabReviewDate(o.lab_order_id, { expires_at: date });
    if (r.success) { setReviewEdit(null); await refresh(); setMsg({ k: 'ok', t: 'Review date updated.' }); }
    else setMsg({ k: 'err', t: r.error || 'Could not update the review date.' });
  };
  const onDiscontinue = async (o) => {
    const r = await cancelLabOrder(o.lab_order_id, { reason: 'Standing order discontinued' });
    if (r.success) { await refresh(); setMsg({ k: 'ok', t: 'Standing order discontinued.' }); }
    else setMsg({ k: 'err', t: r.error || 'Could not discontinue.' });
  };

  const fieldCls =
    'rounded-lg px-2.5 py-2 text-sm border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100';
  const miniBtn =
    'rounded-md px-2 py-1 text-xs font-semibold border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700';

  return (
    <div className="rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-3">
      <div className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-2">🧪 Lab tests for this visit</div>

      {/* When showList is false the caller (LabPanel) renders the richer
          filtered LabSummary instead; this panel contributes the advise form. */}
      {showList && (advised.length === 0 ? (
        <div className="text-sm text-slate-400 dark:text-slate-500 italic">No lab tests advised yet.</div>
      ) : (
        <div className="space-y-1.5">
          {advised.map((o) => (
            <div
              key={o.lab_order_id}
              className="flex flex-wrap items-center gap-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5"
            >
              <span className="font-semibold text-slate-800 dark:text-slate-100">{o.test_name}</span>
              <span className={`text-xs rounded-full px-2 py-0.5 ${isStandingLabOrder(o)
                ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
                {labStatusLabel(o.status)}
              </span>
              {isStandingLabOrder(o) ? (
                <>
                  {o.expires_at && (
                    <span className="text-xs text-slate-500 dark:text-slate-400">till {o.expires_at}</span>
                  )}
                  {!readOnly && (
                    <span className="ml-auto flex items-center gap-1.5">
                      <button type="button" className={miniBtn} onClick={() => onPerform(o)}>🔁 Perform</button>
                      {reviewEdit?.id === o.lab_order_id ? (
                        <>
                          <input
                            type="date"
                            value={reviewEdit.date}
                            min={plusDaysISO(0)}
                            onChange={(e) => setReviewEdit({ id: o.lab_order_id, date: e.target.value })}
                            className={fieldCls + ' !px-2 !py-1 text-xs'}
                          />
                          <button type="button" className={miniBtn} onClick={() => onSaveReview(o)}>Save</button>
                          <button type="button" className={miniBtn} onClick={() => setReviewEdit(null)}>✕</button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className={miniBtn}
                          onClick={() => setReviewEdit({ id: o.lab_order_id, date: o.expires_at || plusDaysISO(30) })}
                        >📅 Review date</button>
                      )}
                      <button type="button" className={miniBtn} onClick={() => onDiscontinue(o)}>✕ Stop</button>
                    </span>
                  )}
                </>
              ) : (
                <>
                  {o.assigned_to?.member_id ? (
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      🔬 {o.assigned_to.name || o.assigned_to.member_id}
                    </span>
                  ) : (
                    <span className="text-xs text-amber-600 dark:text-amber-400">awaiting assignment</span>
                  )}
                  <span className="ml-auto">
                    <LabReportActions order={o} reportOnly={readOnly} onChanged={() => fetchForVisit(visitId, { includeAdvised, includeCompleted })} />
                  </span>
                </>
              )}
            </div>
          ))}
        </div>
      ))}

      {!readOnly && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select className={`${fieldCls} min-w-[180px]`} value={testId} onChange={(e) => setTestId(e.target.value)}>
            <option value="">＋ Add lab test…</option>
            {labTests.map((t) => {
              const done = lastDoneLabel(lastDone[t.procedure_id]);
              return (
                <option key={t.procedure_id} value={t.procedure_id}>
                  {t.name} — ₹{Number(t.default_cost || 0).toFixed(0)}{done ? ` · ${done}` : ''}
                </option>
              );
            })}
          </select>
          <button
            onClick={submit}
            disabled={busy || !testId}
            className="rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-60"
          >
            {busy ? 'Saving…' : '+ Advise'}
          </button>
        </div>
      )}

      {!readOnly && testId && (
        <div className="mt-3 max-w-xs">
          <ValidityControl
            expiresAt={expiresAt}
            standing={isStanding}
            oneTimeOnly={!!pickedTest?.one_time_only}
            oneTimeOnlyReason={pickedTest?.one_time_only_reason}
            onChange={({ expiresAt: ea, standing }) => {
              setExpiresAt(ea);
              setIsStanding(standing);
            }}
          />
        </div>
      )}

      {labTests.length === 0 && (
        <div className="text-xs text-slate-400 dark:text-slate-500 mt-2">
          No lab tests in the catalog yet — an admin adds them under Lab Catalog.
        </div>
      )}
      {msg && (
        <div className={`text-xs mt-2 ${msg.k === 'ok' ? 'text-teal-600 dark:text-teal-400' : 'text-rose-600 dark:text-rose-400'}`}>
          {msg.t}
        </div>
      )}
    </div>
  );
}
